/**
 * Python import 문 파싱 및 라이브러리 의존성 관리 유틸리티
 */

/**
 * 패키지 이름과 import 이름이 다른 경우 매핑
 * key: import 이름, value: 패키지 이름
 */
const IMPORT_TO_PACKAGE_MAP: Record<string, string> = {
  sklearn: 'scikit-learn',
  PIL: 'pillow',
  cv2: 'opencv-python',
  bs4: 'beautifulsoup4',
  yaml: 'pyyaml',
  OpenSSL: 'pyopenssl',
};

const PYTHON_BUILTIN_MODULES = new Set([
  'abc',
  'aifc',
  'argparse',
  'array',
  'ast',
  'asynchat',
  'asyncio',
  'asyncore',
  'atexit',
  'audioop',
  'base64',
  'bdb',
  'binascii',
  'binhex',
  'bisect',
  'builtins',
  'bz2',
  'calendar',
  'cgi',
  'cgitb',
  'chunk',
  'cmath',
  'cmd',
  'code',
  'codecs',
  'codeop',
  'collections',
  'colorsys',
  'compileall',
  'concurrent',
  'configparser',
  'contextlib',
  'contextvars',
  'copy',
  'copyreg',
  'cProfile',
  'crypt',
  'csv',
  'ctypes',
  'curses',
  'dataclasses',
  'datetime',
  'dbm',
  'decimal',
  'difflib',
  'dis',
  'distutils',
  'doctest',
  'email',
  'encodings',
  'enum',
  'errno',
  'faulthandler',
  'fcntl',
  'filecmp',
  'fileinput',
  'fnmatch',
  'formatter',
  'fractions',
  'ftplib',
  'functools',
  'gc',
  'getopt',
  'getpass',
  'gettext',
  'glob',
  'graphlib',
  'grp',
  'gzip',
  'hashlib',
  'heapq',
  'hmac',
  'html',
  'http',
  'imaplib',
  'imghdr',
  'imp',
  'importlib',
  'inspect',
  'io',
  'ipaddress',
  'itertools',
  'json',
  'keyword',
  'lib2to3',
  'linecache',
  'locale',
  'logging',
  'lzma',
  'mailbox',
  'mailcap',
  'marshal',
  'math',
  'mimetypes',
  'mmap',
  'modulefinder',
  'multiprocessing',
  'netrc',
  'nis',
  'nntplib',
  'numbers',
  'operator',
  'optparse',
  'os',
  'ossaudiodev',
  'parser',
  'pathlib',
  'pdb',
  'pickle',
  'pickletools',
  'pipes',
  'pkgutil',
  'platform',
  'plistlib',
  'poplib',
  'posix',
  'posixpath',
  'pprint',
  'profile',
  'pstats',
  'pty',
  'pwd',
  'py_compile',
  'pyclbr',
  'pydoc',
  'queue',
  'quopri',
  'random',
  're',
  'readline',
  'reprlib',
  'resource',
  'rlcompleter',
  'runpy',
  'sched',
  'secrets',
  'select',
  'selectors',
  'shelve',
  'shlex',
  'shutil',
  'signal',
  'site',
  'smtpd',
  'smtplib',
  'sndhdr',
  'socket',
  'socketserver',
  'spwd',
  'sqlite3',
  'ssl',
  'stat',
  'statistics',
  'string',
  'stringprep',
  'struct',
  'subprocess',
  'sunau',
  'symbol',
  'symtable',
  'sys',
  'sysconfig',
  'syslog',
  'tabnanny',
  'tarfile',
  'telnetlib',
  'tempfile',
  'termios',
  'test',
  'textwrap',
  'threading',
  'time',
  'timeit',
  'tkinter',
  'token',
  'tokenize',
  'tomllib',
  'trace',
  'traceback',
  'tracemalloc',
  'tty',
  'turtle',
  'turtledemo',
  'types',
  'typing',
  'unicodedata',
  'unittest',
  'urllib',
  'uu',
  'uuid',
  'venv',
  'warnings',
  'wave',
  'weakref',
  'webbrowser',
  'winreg',
  'winsound',
  'wsgiref',
  'xdrlib',
  'xml',
  'xmlrpc',
  'zipapp',
  'zipfile',
  'zipimport',
  'zlib',
  '_thread',
]);

/**
 * Python 파일에서 import된 라이브러리 목록 추출 (표준 라이브러리 제외)
 */
export function parseImportsFromPythonCode(code: string): string[] {
  const imports = new Set<string>();
  const lines = code.split('\n');

  for (const line of lines) {
    const trimmed = line.trim();

    if (trimmed.startsWith('#')) continue;

    const importMatch = trimmed.match(/^import\s+([a-zA-Z_][a-zA-Z0-9_]*)/);
    if (importMatch) {
      const moduleName = importMatch[1];
      if (!PYTHON_BUILTIN_MODULES.has(moduleName)) {
        imports.add(moduleName);
      }
      continue;
    }

    const fromImportMatch = trimmed.match(/^from\s+([a-zA-Z_][a-zA-Z0-9_]*)/);
    if (fromImportMatch) {
      const moduleName = fromImportMatch[1];
      if (!PYTHON_BUILTIN_MODULES.has(moduleName)) {
        imports.add(moduleName);
      }
      continue;
    }
  }

  return Array.from(imports);
}

/**
 * import된 라이브러리가 모두 venv_packages에 있는지 확인
 */
export function validateImportDependencies(
  imports: string[],
  availablePackages: string[]
): { isValid: boolean; missingPackages: string[] } {
  const availableSet = new Set(availablePackages);
  const missingPackages = imports.filter((imp) => {
    // import 이름이 그대로 패키지로 있는지 확인
    if (availableSet.has(imp)) {
      return false;
    }

    // import 이름이 매핑된 패키지 이름으로 있는지 확인
    const packageName = IMPORT_TO_PACKAGE_MAP[imp];
    if (packageName && availableSet.has(packageName)) {
      return false;
    }

    return true;
  });

  return {
    isValid: missingPackages.length === 0,
    missingPackages,
  };
}

/**
 * custom 라이브러리의 의존성 맵 생성
 */
export type LibraryDependencyMap = Record<string, string[]>;

export function buildDependencyMap(
  uploadedModules: Array<{ module_name: string; dependencies?: string[] }>
): LibraryDependencyMap {
  const map: LibraryDependencyMap = {};

  for (const module of uploadedModules) {
    if (module.dependencies && module.dependencies.length > 0) {
      map[module.module_name] = module.dependencies;
    }
  }

  return map;
}

/**
 * 특정 라이브러리에 의존하는 설치된 custom 라이브러리 찾기
 */
export function findDependentLibraries(
  targetLibrary: string,
  installedCustomLibraries: string[],
  dependencyMap: LibraryDependencyMap
): string[] {
  const dependents: string[] = [];

  for (const customLib of installedCustomLibraries) {
    const deps = dependencyMap[customLib] || [];
    if (deps.includes(targetLibrary)) {
      dependents.push(customLib);
    }
  }

  return dependents;
}

/**
 * custom 라이브러리 설치 시 필요한 모든 의존성 라이브러리 찾기
 */
export function getRequiredDependencies(
  customLibrary: string,
  dependencyMap: LibraryDependencyMap,
  currentlyInstalled: string[]
): string[] {
  const deps = dependencyMap[customLibrary] || [];
  const installedSet = new Set(currentlyInstalled);

  return deps.filter((dep) => !installedSet.has(dep));
}

/**
 * import 이름을 실제 패키지 이름으로 변환
 */
export function normalizeImportToPackageName(importName: string, availablePackages: string[]): string {
  // 이미 패키지 이름으로 사용 가능한 경우 그대로 반환
  if (availablePackages.includes(importName)) {
    return importName;
  }

  // 매핑 테이블에서 패키지 이름 찾기
  const packageName = IMPORT_TO_PACKAGE_MAP[importName];
  if (packageName && availablePackages.includes(packageName)) {
    return packageName;
  }

  // 매핑이 없으면 import 이름 그대로 반환
  return importName;
}
